/**
 * The sidebar's content and visibility rules, as pure functions.
 *
 * Everything here is derivation, deliberately separated from the components
 * that render it: F-NAV-09's six state words, F-NAV-08's two chips and
 * F-NAV-12's manual-override guard are the parts that are easy to get subtly
 * wrong and cheap to test, and none of them needs a DOM to be checked.
 */

import { HEAVY_BAG_45_COPY } from "@ff/domain";
import type { ShellActivity } from "../app-state/activity";
import type { DestinationId } from "./destinations";

// ---------------------------------------------------------------------------
// F-NAV-07 — sidebar groups
// ---------------------------------------------------------------------------

/**
 * The Build destination presents two tools. F-NAV-07 makes a Build row
 * "selected" only when the destination **and** the tool match, so the tool is
 * part of the shell's selection state rather than the Build feature's private
 * business.
 */
export type BuildTool = "workouts" | "combo";

export interface SidebarDestinationRow {
  readonly id: string;
  readonly title: string;
  readonly subtitle: string;
  readonly destination: DestinationId;
  /** Present only for Build rows; `undefined` means the destination alone selects. */
  readonly tool?: BuildTool;
  /**
   * F-NAV-07: Combo Studio is the only destination row with no shortcut, so a
   * row that has one is the exception being encoded, not a default being
   * omitted.
   */
  readonly hasShortcut: boolean;
}

export interface SidebarGroup {
  readonly title: string;
  readonly rows: readonly SidebarDestinationRow[];
}

export const SIDEBAR_GROUPS: readonly SidebarGroup[] = [
  {
    title: "Start",
    rows: [
      {
        id: "sidebar.cornerBoard",
        title: "Corner Board",
        subtitle: "Plan the next bell",
        destination: "train",
        hasShortcut: true,
      },
    ],
  },
  {
    title: "Build",
    rows: [
      {
        id: "sidebar.workoutTape",
        title: "Workout Tape",
        subtitle: "Structure, timing, and cues",
        destination: "build",
        tool: "workouts",
        hasShortcut: true,
      },
      {
        id: "sidebar.comboStudio",
        title: "Combo Studio",
        subtitle: "Compose and rehearse",
        destination: "build",
        tool: "combo",
        hasShortcut: false,
      },
    ],
  },
  {
    title: "Reflect",
    rows: [
      {
        id: "sidebar.cornerBook",
        title: "Corner Book",
        subtitle: "Private Journal",
        destination: "journal",
        hasShortcut: true,
      },
    ],
  },
  {
    title: "Equipment",
    rows: [
      {
        id: "sidebar.settings",
        title: "Settings",
        subtitle: "Cues, drills, and privacy",
        destination: "settings",
        hasShortcut: true,
      },
    ],
  },
];

export function isSidebarRowSelected(
  row: SidebarDestinationRow,
  destination: DestinationId,
  buildTool: BuildTool,
): boolean {
  if (row.destination !== destination) return false;
  if (row.tool === undefined) return true;
  return row.tool === buildTool;
}

// ---------------------------------------------------------------------------
// F-NAV-08 — the Ringside quick action
// ---------------------------------------------------------------------------

export interface RingsideQuickAction {
  readonly chip: "LIVE" | "READY";
  readonly subtitle: string;
  /** Resume an existing effort, or start the Heavy Bag 45 template. */
  readonly intent: "resume" | "start";
}

export const RINGSIDE_ACTION_TITLE = "Ringside" as const;

export function ringsideQuickAction(activity: ShellActivity): RingsideQuickAction {
  const hasEffort = activity.openEffort !== null || activity.structured !== null;
  return hasEffort
    ? { chip: "LIVE", subtitle: "Continue the active effort", intent: "resume" }
    : { chip: "READY", subtitle: `${HEAVY_BAG_45_COPY.name} ready`, intent: "start" };
}

// ---------------------------------------------------------------------------
// F-NAV-09 — the resume card
// ---------------------------------------------------------------------------

export type ResumeCardTint = "warning" | "signal";

export interface ResumeCard {
  readonly state: string;
  readonly title: string;
  readonly tint: ResumeCardTint;
}

/** The fallback pair, used when an effort exists but carries no usable name. */
export const RESUME_CARD_FALLBACK_STATE = "EFFORT" as const;
export const RESUME_CARD_FALLBACK_TITLE = "Resume effort" as const;

function named(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * Open-effort state always outranks structured state: an open effort is the one
 * that can be *lost*, and showing "Continue Heavy Bag 45" while an unsaved
 * effort waits behind it points the user away from the only unsafe record.
 */
export function resumeCard(activity: ShellActivity): ResumeCard | null {
  const { openEffort, structured } = activity;

  if (openEffort !== null) {
    const activityName = named(openEffort.activity);
    if (openEffort.state === "finishedUnsaved") {
      return activityName === null
        ? { state: RESUME_CARD_FALLBACK_STATE, title: RESUME_CARD_FALLBACK_TITLE, tint: "warning" }
        : { state: "SAVE NEEDED", title: `Finish saving ${activityName}`, tint: "warning" };
    }
    const state = openEffort.state === "active" ? "ACTIVE EFFORT" : "PAUSED EFFORT";
    return activityName === null
      ? { state: RESUME_CARD_FALLBACK_STATE, title: RESUME_CARD_FALLBACK_TITLE, tint: "signal" }
      : { state, title: `Continue ${activityName}`, tint: "signal" };
  }

  if (structured !== null) {
    const planName = named(structured.planName);
    const state = structured.state === "active" ? "ACTIVE WORKOUT" : "PAUSED WORKOUT";
    return planName === null
      ? { state: RESUME_CARD_FALLBACK_STATE, title: RESUME_CARD_FALLBACK_TITLE, tint: "signal" }
      : { state, title: `Continue ${planName}`, tint: "signal" };
  }

  return null;
}

// ---------------------------------------------------------------------------
// F-NAV-11 — the status block
// ---------------------------------------------------------------------------

export const STATUS_PRIVACY_LINE = "LOCAL + PRIVATE" as const;
export const STATUS_READY_LINE = "Ready to start · Voice + bells" as const;

export interface SidebarStatus {
  readonly privacy: string;
  readonly title: string;
  readonly state: string;
}

export function sidebarStatus(activity: ShellActivity): SidebarStatus {
  const card = resumeCard(activity);
  return {
    privacy: STATUS_PRIVACY_LINE,
    title: card?.title ?? HEAVY_BAG_45_COPY.name,
    state: card?.state ?? STATUS_READY_LINE,
  };
}

// ---------------------------------------------------------------------------
// F-NAV-12 — visibility and the manual-override guard
// ---------------------------------------------------------------------------

export interface SidebarVisibility {
  readonly visible: boolean;
  /**
   * What the automatic rule said the last time it was consulted. Comparing
   * against it — rather than re-applying the rule every render — is what makes
   * a manual toggle stick: the automatic value has to actually *flip* before it
   * is allowed to overrule the user.
   */
  readonly lastAutomatic: boolean;
}

export function initialSidebarVisibility(automatic: boolean): SidebarVisibility {
  return { visible: automatic, lastAutomatic: automatic };
}

export function reconcileSidebarVisibility(
  current: SidebarVisibility,
  automatic: boolean,
): SidebarVisibility {
  if (automatic === current.lastAutomatic) return current;
  return { visible: automatic, lastAutomatic: automatic };
}

export function toggleSidebarVisibility(current: SidebarVisibility): SidebarVisibility {
  return { visible: !current.visible, lastAutomatic: current.lastAutomatic };
}
